import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import type {
  ListUnmatchedQrSignatures,
  MatchQrSignature,
  UnmatchedQrSignature,
} from '@/shared/domain'
import { formatShortDate } from '@/shared/infra/format'
import { reportError } from '@/shared/infra/observability'
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/primitives'

export type MatchQrSignaturePickerProps = {
  open: boolean
  orgId: string
  orgType: 'club' | 'outfitter'
  templateId: string | null
  targetUserId: string | null
  personName: string
  onListUnmatchedQrSignatures: ListUnmatchedQrSignatures
  onMatchQrSignature: MatchQrSignature
  onMatched: () => void
  onClose: () => void
}

const LIST_LIMIT = 50

export function MatchQrSignaturePicker({
  open,
  orgId,
  orgType,
  templateId,
  targetUserId,
  personName,
  onListUnmatchedQrSignatures,
  onMatchQrSignature,
  onMatched,
  onClose,
}: MatchQrSignaturePickerProps) {
  const [signatures, setSignatures] = useState<UnmatchedQrSignature[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [matchingId, setMatchingId] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !templateId) {
      setSignatures([])
      setErrorMessage(null)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setErrorMessage(null)
    onListUnmatchedQrSignatures({ templateId, limit: LIST_LIMIT })
      .then((result) => {
        if (cancelled) return
        setSignatures(result.signatures)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = reportError(err, {
          fallbackMessage: 'Failed to load unmatched signatures. Please try again.',
        })
        setErrorMessage(message)
        setSignatures([])
      })
      .finally(() => {
        if (cancelled) return
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, templateId, onListUnmatchedQrSignatures])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && matchingId === null) onClose()
    },
    [matchingId, onClose],
  )

  const handleMatch = useCallback(
    async (signature: UnmatchedQrSignature) => {
      if (!targetUserId) return
      setMatchingId(signature.id)
      setErrorMessage(null)
      try {
        await onMatchQrSignature({
          requestId: signature.id,
          orgId,
          orgType,
          userId: targetUserId,
        })
        onMatched()
        onClose()
      } catch (err) {
        const message = reportError(err, {
          fallbackMessage: 'Failed to match the signature. Please try again.',
        })
        setErrorMessage(message)
      } finally {
        setMatchingId(null)
      }
    },
    [targetUserId, orgId, orgType, onMatchQrSignature, onMatched, onClose],
  )

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Match a QR signature</SheetTitle>
          <SheetDescription>
            Attach a signed document to {personName} so their compliance updates.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-2 px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : errorMessage ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {errorMessage}
            </div>
          ) : signatures.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No unmatched QR signatures for this waiver.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {signatures.map((signature) => (
                <li
                  key={signature.id}
                  className="flex items-center justify-between gap-3 rounded-md border bg-card p-3"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-sm font-medium">
                      {signature.signerName ?? 'Unnamed signer'}
                    </span>
                    {signature.signerEmail ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {signature.signerEmail}
                      </span>
                    ) : null}
                    {signature.requestedAt ? (
                      <span className="text-2xs text-muted-foreground">
                        Signed {formatShortDate(signature.requestedAt)}
                      </span>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!targetUserId || matchingId !== null}
                    onClick={() => void handleMatch(signature)}
                  >
                    {matchingId === signature.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      'Match'
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
